import { useMemo } from "react"

import type { MetricRecord } from "@/metrics/runtime"

import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import { useConversationMetricsWorkspaceSettingsTransport } from "../routes/useConversationMetricsConfigurationTransport"
import { useConversationsMetricLabelsTransport } from "../transport"
import { useLatestConversationClusteringRunTransport } from "../transport/hooks/useLatestConversationClusteringRunTransport"
import type { ColumnCallbacks } from "../types/filterState"
import type {
  ConversationsMetricLabelSourcesById,
  ConversationsMetricTableRenderersById,
} from "../types/metricAdapters"
import {
  buildClusterDescriptionsBySimulationOutputId,
  CLUSTER_DESCRIPTION_COLUMN_KEY,
  withClusterDescriptions,
} from "../utils/conversationClusterDescriptions"
import {
  buildConversationsTableColumns,
  getConversationsMetricIds,
} from "../utils/conversationsTableState"
import {
  UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS,
  type VirtualConversationsTableColumn,
} from "../utils/getResponsiveConversationsColumns"
import { useAllMetrics } from "./useAllMetrics"
import { useConversationsColumns } from "./useConversationsColumns"

interface UseConversationsMetricTableStateParams {
  allConversationsData: ConversationsTableResult[]
  /** Joins per-conversation descriptions from the latest clustering run. */
  clusterDescriptionsEnabled: boolean
  metricIds: string[]
  registerColumnCallbacks: (callbacks: ColumnCallbacks) => () => void
  tableHasInitiallyLoaded: boolean
}

export function useConversationsMetricTableState({
  allConversationsData,
  clusterDescriptionsEnabled,
  metricIds,
  registerColumnCallbacks,
  tableHasInitiallyLoaded,
}: UseConversationsMetricTableStateParams) {
  const { data: latestClusteringRun } =
    useLatestConversationClusteringRunTransport({
      enabled: clusterDescriptionsEnabled,
    })
  const clusterDescriptions = useMemo(
    () =>
      clusterDescriptionsEnabled && latestClusteringRun
        ? buildClusterDescriptionsBySimulationOutputId(latestClusteringRun)
        : new Map<string, string>(),
    [clusterDescriptionsEnabled, latestClusteringRun],
  )
  const hasClusterDescriptions = clusterDescriptions.size > 0
  const tableRows = useMemo(
    () => withClusterDescriptions(allConversationsData, clusterDescriptions),
    [allConversationsData, clusterDescriptions],
  )
  const {
    data: workspaceSettings,
    isError: isWorkspaceSettingsError,
    isLoading: isWorkspaceSettingsLoading,
  } = useConversationMetricsWorkspaceSettingsTransport()
  const defaultConversationMetrics = useMemo<string[]>(() => {
    if (isWorkspaceSettingsError || isWorkspaceSettingsLoading) {
      return []
    }
    return workspaceSettings?.default_metrics || []
  }, [isWorkspaceSettingsError, isWorkspaceSettingsLoading, workspaceSettings])
  const metricLabelIds = useMemo(
    () =>
      getConversationsMetricIds(allConversationsData, [
        ...metricIds,
        ...defaultConversationMetrics,
      ]),
    [allConversationsData, defaultConversationMetrics, metricIds],
  )
  const { data: compactMetricLabelsById = {} } =
    useConversationsMetricLabelsTransport({
      enabled: tableHasInitiallyLoaded,
      metricIds: metricLabelIds,
    })
  const { data: metricRecordsData, isLoading: isMetricsLoading } =
    useAllMetrics({ enabled: tableHasInitiallyLoaded })
  const metricRecords = useMemo(() => {
    if (!metricRecordsData) {
      return {}
    }
    return Object.fromEntries(
      Object.values(metricRecordsData)
        .filter((metric) => metric.id)
        .map((metric) => [metric.id, metric]),
    ) as Record<string, MetricRecord>
  }, [metricRecordsData])
  const metricLabelsById = useMemo<ConversationsMetricLabelSourcesById>(
    () => ({
      ...compactMetricLabelsById,
      ...Object.fromEntries(
        Object.entries(metricRecords).map(([metricId, metric]) => [
          metricId,
          { metric_display_name: metric.metric_display_name },
        ]),
      ),
    }),
    [compactMetricLabelsById, metricRecords],
  )
  const metricDisplayNames = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(metricLabelsById).map(([metricId, label]) => [
          metricId,
          label.metric_display_name ?? metricId,
        ]),
      ),
    [metricLabelsById],
  )
  const metricRenderersById = useMemo<ConversationsMetricTableRenderersById>(
    () =>
      Object.fromEntries(
        Object.entries(metricRecords).map(([metricId, metric]) => [
          metricId,
          {
            renderValueOnlyTableCell:
              metric.renderValueOnlyTableCell.bind(metric),
          },
        ]),
      ),
    [metricRecords],
  )
  const defaultConversationMetricKeys = useMemo(() => {
    if (!metricRecordsData) {
      return defaultConversationMetrics
    }
    const metricIdsByName = new Map(
      Object.values(metricRecordsData).map((metric) => [
        metric.metric_name,
        metric.id,
      ]),
    )
    return defaultConversationMetrics.map(
      (metricKey) => metricIdsByName.get(metricKey) ?? metricKey,
    )
  }, [defaultConversationMetrics, metricRecordsData])
  const { visibleColumns, handleVisibleColumnsReset } = useConversationsColumns(
    {
      allConversationsData,
      defaultConversationMetrics: defaultConversationMetricKeys,
      hasClusterDescriptions,
      registerColumnCallbacks,
    },
  )
  const allColumns = useMemo((): VirtualConversationsTableColumn[] => {
    const columns: VirtualConversationsTableColumn[] = [
      {
        key: "created_at",
        label: "Uploaded",
        width: UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS.timestamp,
        fullLabel: "Uploaded",
      },
      {
        key: "occurred_at",
        label: "Occurred",
        width: UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS.timestamp,
        fullLabel: "Occurred",
      },
      {
        key: "audio",
        label: "Audio",
        width: UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS.audio,
        fullLabel: "Audio",
      },
      ...(hasClusterDescriptions
        ? [
            {
              key: CLUSTER_DESCRIPTION_COLUMN_KEY,
              label: "Description",
              width: UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS.description,
              fullLabel: "Description",
            },
          ]
        : []),
      {
        key: "customer_metadata",
        label: "Metadata",
        width: UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS.metadata,
        fullLabel: "Metadata",
      },
    ]
    columns.push({
      key: "reviewer_assignees",
      label: "Reviewer",
      width: UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS.standard,
      fullLabel: "Reviewer",
    })
    columns.push(
      {
        key: "tags",
        label: "Tags",
        width: UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS.standard,
        fullLabel: "Tags",
      },
      {
        key: "simulation_output_id",
        label: "ID",
        width: UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS.standard,
        fullLabel: "ID",
      },
    )
    return columns
  }, [hasClusterDescriptions])
  const knownMetricIds = useMemo(
    () => new Set(Object.keys(metricRecords)),
    [metricRecords],
  )
  const columnsWithMetrics = useMemo(
    () =>
      buildConversationsTableColumns({
        allColumns,
        allConversationsData,
        defaultConversationMetrics: defaultConversationMetricKeys,
        metricLabelsById,
        knownMetricIds,
      }),
    [
      allColumns,
      allConversationsData,
      defaultConversationMetricKeys,
      metricLabelsById,
      knownMetricIds,
    ],
  )

  return {
    columnsWithMetrics,
    handleVisibleColumnsReset,
    isMetricsLoading,
    isWorkspaceSettingsError,
    metricDisplayNames,
    metricLabelsById,
    metricRecords,
    metricRenderersById,
    tableRows,
    visibleColumns,
  }
}
